import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { alertDialog, getTelegram, isInTelegram } from '../telegram';
import {
  getMe,
  getNotificationPrefs,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  saveNotificationPrefs,
} from '../api';
import { WebBackButton } from '../components/WebNav';
import { notifyNotificationsChanged } from '../components/NotificationBell';
import TelegramLinkCard from '../components/TelegramLinkCard';
import type { AppNotification, NotificationChannel, NotificationKindPrefs } from '../types';

// ============================================================================
// Блок «Уведомления в приложении»: список уведомлений пользователя (с отметкой
// прочтения и переходом к заказу / депозиту) и личные настройки — какие события
// и по каким каналам получать (в приложении / Telegram / e-mail). Одинаково
// работает в Telegram Mini App и на сайте.
// ============================================================================

interface Props {
  onBack: () => void;
  backLabel?: string;
  // Переход к объекту уведомления: заказ → история заказов, депозит → страница «Депозит».
  onOpenEntity?: (entityType: string | null, entityId: string | null) => void;
}

type DirtyPrefs = Record<string, Partial<Record<NotificationChannel, boolean>>>;

const CHANNELS: NotificationChannel[] = ['inapp', 'telegram', 'email'];
const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  inapp: 'В приложении',
  telegram: 'Telegram',
  email: 'E-mail',
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm} ${hh}:${mi}`;
}

const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tg-button)',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  padding: '4px 0',
};

export default function NotificationsPage({ onBack, backLabel = '‹ Назад', onOpenEntity }: Props) {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [kinds, setKinds] = useState<NotificationKindPrefs[]>([]);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [dirty, setDirty] = useState<DirtyPrefs>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Привязка Telegram (миграция 021): на сайте показываем карточку, пока telegram_id пуст.
  const [tgLinked, setTgLinked] = useState<boolean | null>(null);
  // После привязки карточку не убираем: пользователь видит подтверждение «Telegram подключён».
  const [justLinked, setJustLinked] = useState(false);

  // В Telegram — нативная кнопка «назад», в браузере — WebBackButton ниже.
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(onBack);
    return () => { tg.BackButton.offClick(onBack); tg.BackButton.hide(); };
  }, [onBack]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getNotifications(100);
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadPrefs = useCallback(() => {
    getNotificationPrefs()
      .then(({ data }) => setKinds(data.kinds))
      .catch(() => { /* настройки не критичны для списка */ })
      .finally(() => setPrefsLoaded(true));
  }, []);
  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  useEffect(() => {
    if (isInTelegram()) return;
    getMe()
      .then(({ data }) => setTgLinked(!!data.telegram_id))
      .catch(() => { /* карточку просто не покажем */ });
  }, []);

  const open = useCallback(async (n: AppNotification) => {
    if (!n.read_at) {
      const stamp = new Date().toISOString();
      setItems(prev => prev.map(i => (i.id === n.id ? { ...i, read_at: stamp } : i)));
      setUnread(u => Math.max(0, u - 1));
      try { await markNotificationRead(n.id); } catch { /* повторим при следующем открытии */ }
      notifyNotificationsChanged();
    }
    if (onOpenEntity && (n.entity_type === 'order' || n.entity_type === 'deposit')) {
      onOpenEntity(n.entity_type, n.entity_id);
    }
  }, [onOpenEntity]);

  const readAll = useCallback(async () => {
    const stamp = new Date().toISOString();
    setItems(prev => prev.map(i => (i.read_at ? i : { ...i, read_at: stamp })));
    setUnread(0);
    try { await markAllNotificationsRead(); } catch { /* не критично */ }
    notifyNotificationsChanged();
  }, []);

  const toggle = (key: string, ch: NotificationChannel, value: boolean) => {
    setKinds(prev => prev.map(k => (k.key === key ? { ...k, channels: { ...k.channels, [ch]: value } } : k)));
    setDirty(prev => ({ ...prev, [key]: { ...(prev[key] ?? {}), [ch]: value } }));
    setSaved(false);
  };

  const save = useCallback(async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      const { data } = await saveNotificationPrefs(dirty);
      setKinds(data.kinds);
      setDirty({});
      setSaved(true);
    } catch (e) {
      alertDialog(`Не удалось сохранить настройки: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [dirty]);

  const groups = Array.from(new Set(kinds.map(k => k.group)));
  const hasChanges = Object.keys(dirty).length > 0;

  return (
    <div className="container" style={{ padding: '16px 16px 80px' }}>
      <WebBackButton onClick={onBack} label={backLabel} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Уведомления</h2>
        {unread > 0 && (
          <button onClick={readAll} style={linkButton}>Прочитать все ({unread})</button>
        )}
      </div>

      {loading && <p className="hint">Загрузка…</p>}
      {error && <p className="hint">Ошибка: {error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="hint">Уведомлений пока нет. Здесь появятся события по заказам, депозиту и заявкам.</p>
      )}

      {items.map(n => {
        const isRead = !!n.read_at;
        const hasLink = n.entity_type === 'order' || n.entity_type === 'deposit';
        return (
          <div
            key={n.id}
            className="card"
            role="button"
            onClick={() => open(n)}
            style={{
              cursor: 'pointer',
              padding: '12px 14px',
              marginBottom: 8,
              opacity: isRead ? 0.78 : 1,
              borderLeft: isRead ? '3px solid transparent' : '3px solid var(--tg-button)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
              <div style={{ fontWeight: isRead ? 500 : 700, fontSize: 15 }}>{n.title}</div>
              <div className="hint" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtTime(n.created_at)}</div>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.4, marginTop: 4, whiteSpace: 'pre-line' }}>{n.body}</div>
            {hasLink && (
              <div style={{ fontSize: 13, color: 'var(--tg-button)', marginTop: 6, fontWeight: 600 }}>
                {n.entity_type === 'order' ? 'Открыть заказы ›' : 'Открыть депозит ›'}
              </div>
            )}
          </div>
        );
      })}

      {(tgLinked === false || justLinked) && (
        <div style={{ marginTop: 20 }}>
          <TelegramLinkCard onLinked={() => { setTgLinked(true); setJustLinked(true); loadPrefs(); }} />
        </div>
      )}

      <h3 style={{ margin: '24px 0 4px', fontSize: 17 }}>Настройки уведомлений</h3>
      <p className="hint" style={{ margin: '0 0 12px' }}>
        Выберите, какие события и каким способом получать. «В приложении» — это колокольчик на главном экране.
      </p>
      {!prefsLoaded && <p className="hint">Загрузка…</p>}

      {groups.map(group => (
        <div key={group} style={{ marginBottom: 14 }}>
          <div className="hint" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, margin: '0 0 6px' }}>
            {group}
          </div>
          {kinds.filter(k => k.group === group).map(k => (
            <div key={k.key} className="card" style={{ padding: '10px 14px', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{k.label}</div>
              <div className="hint" style={{ fontSize: 12, marginBottom: 8 }}>{k.description}</div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {CHANNELS.map(ch => {
                  const available = k.available[ch];
                  const reason = k.unavailable_reason[ch];
                  return (
                    <label
                      key={ch}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: available ? 1 : 0.5 }}
                    >
                      <input
                        type="checkbox"
                        checked={k.channels[ch]}
                        disabled={!available}
                        onChange={e => toggle(k.key, ch, e.target.checked)}
                      />
                      {CHANNEL_LABEL[ch]}
                      {!available && reason ? <span className="hint" style={{ fontSize: 11 }}>({reason})</span> : null}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}

      {prefsLoaded && kinds.length > 0 && (
        <button
          className="primary"
          onClick={save}
          disabled={saving || !hasChanges}
          style={{ opacity: hasChanges ? 1 : 0.6 }}
        >
          {saving ? 'Сохраняем…' : saved && !hasChanges ? 'Сохранено ✓' : 'Сохранить настройки'}
        </button>
      )}
    </div>
  );
}
